/** Minimal parser and renderer for named placeholder tags in translation templates. */
import React, { ComponentType, ReactElement, ReactNode } from 'react';

/** Node in the parsed template tree. */
export type TrNode =
  | Readonly<{ type: 'text'; content: string }>
  | Readonly<{ type: 'tag'; name: string; children: TrNode[] }>;

/** Mapping value type: either an intrinsic React element or a component to render. */
export type Mapped = ReactElement | ComponentType<{ children?: ReactNode }>;

/** Mapping from named tag to element/component. Positional placeholders are not supported. */
export type ComponentsMap = Readonly<Record<string, Mapped>>;

type Frame = { name: string; children: TrNode[] };

/** Strict named-tag pattern. No attributes, no entities, only opening/closing tags. */
const TAG_PATTERN = '<(\\/?)([A-Za-z_][\\w-]*)\\s*>';

function getMapped(components: ComponentsMap, key: string): Mapped | undefined {
  return components[key];
}

function emitText(target: Frame, text: string): void {
  if (!text) return;
  target.children.push({ type: 'text', content: text });
}
function handleOpen(stack: Frame[], name: string): void {
  stack.push({ name, children: [] });
}

function handleClose(stack: Frame[], name: string, raw: string): void {
  const top = stack[stack.length - 1];
  if (top.name !== name) {
    emitText(stack[stack.length - 1], raw);
    return;
  }
  stack.pop();
  const parent = stack[stack.length - 1];
  parent.children.push({ type: 'tag', name, children: top.children });
}

function finalizeUnclosed(stack: Frame[]): void {
  while (stack.length > 1) {
    const unclosed = stack.pop();
    if (!unclosed) break;
    emitText(stack[stack.length - 1], `<${unclosed.name}>`);
    for (const child of unclosed.children) {
      stack[stack.length - 1].children.push(child);
    }
  }
}

/**
 * Parse a translation template with named placeholder tags into a simple tree.
 * - Accepts only named tags like &lt;link&gt;...&lt;/link&gt;.
 * - Treats malformed or mismatched tags as plain text.
 * - Ignores attributes/self-closing syntax.
 */
export function parseTemplate(template: string): TrNode[] {
  const root: Frame = { name: '_root', children: [] };
  const stack: Frame[] = [root];

  let lastIndex = 0;
  let match: RegExpExecArray | null;

  const tagRe = new RegExp(TAG_PATTERN, 'g');

  while ((match = tagRe.exec(template)) !== null) {
    const [raw, slash, name] = match;
    const idx = match.index;

    if (idx > lastIndex) {
      emitText(stack[stack.length - 1], template.slice(lastIndex, idx));
    }

    if (slash === '/') {
      handleClose(stack, name, raw);
    } else {
      handleOpen(stack, name);
    }

    lastIndex = idx + raw.length;
  }

  if (lastIndex < template.length) {
    emitText(stack[stack.length - 1], template.slice(lastIndex));
  }

  finalizeUnclosed(stack);

  return root.children;
}

/**
 * Render a parsed template tree using the provided named components map.
 * Unknown tags are stripped and only their children are rendered.
 */
export function renderNodes(nodes: TrNode[], components: ComponentsMap, keyPrefix = 'k'): ReactNode {
  return nodes.map((node, i) => {
    const key = `${keyPrefix}-${i}`;
    if (node.type === 'text') return node.content;

    const mapped = getMapped(components, node.name);
    const children = renderNodes(node.children, components, key);

    if (!mapped) return <React.Fragment key={key}>{children}</React.Fragment>;
    if (typeof mapped === 'function') {
      const Comp = mapped as ComponentType<{ children?: ReactNode }>;
      return <Comp key={key}>{children}</Comp>;
    }
    const el = mapped as ReactElement;
    return React.cloneElement(el, { key: el.key ?? key }, children);
  });
}
